<script setup>
import { computed, ref, shallowRef } from 'vue';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import SettingsSection from './SettingsSection.vue';
import ProfilePhoto from './ProfilePhoto.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { RoleCode } from '@/constants/accounts';
import { useConfirm } from '@/composables/useConfirm';
import { useProfileSettings } from '@/composables/useProfileSettings';
import { useToast } from '@/composables/useToast';
import { required } from '@/utils/validation';

// Settings → Profile: photo, the account's designations, and personal
// details. Email and designations are managed by an administrator, so they
// are shown read-only.
const emit = defineEmits(['reloadProfile']);

const SEX_OPTIONS = ['Male', 'Female'];
const mobile = (v) =>
  !v || /^\+?\d{10,13}$/.test(String(v).replace(/[\s-]/g, '')) ||
  'Enter a valid mobile number.';

const store = useStore();
const toast = useToast();
const formRef = ref(null);
const {
  form,
  photo,
  hasPhoto,
  loading,
  saving,
  uploading,
  removing,
  savedAt,
  isDirty,
  reset,
  save,
  uploadPhoto,
  removePhoto,
} = useProfileSettings();
const { confirm } = useConfirm();

const email = computed(() => store.state.user?.user?.email || '');

async function onSave() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  try {
    await save();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save your profile.'));
  }
}

async function onPhoto(file) {
  try {
    await uploadPhoto(file);
    emit('reloadProfile');
    toast.success('Profile photo updated.');
  } catch (e) {
    toast.error(errorMessage(e, e.message || 'Could not upload the photo.'));
  }
}

async function onRemovePhoto() {
  const ok = await confirm({
    title: 'Remove your profile photo?',
    description: 'You go back to the default picture until you upload a new one.',
    confirmText: 'Remove photo',
    destructive: true,
  });
  if (!ok) return;
  try {
    await removePhoto();
    emit('reloadProfile');
    toast.success('Profile photo removed.');
  } catch (e) {
    toast.error(errorMessage(e, 'Could not remove the photo.'));
  }
}
const today = new Date().toISOString().slice(0, 10);

// Designations (JHS/SHS Admin, Prefect, Class Adviser, Subject Teacher…) and
// the sections the account advises. Loaded on its own so the form above
// doesn't wait for it.
const designations = shallowRef(null); // { roles, advisory }
const designationsError = shallowRef('');
const ROLE_BADGE = {
  [RoleCode.Superadmin]: 'blue',
  [RoleCode.SchoolAdmin]: 'blue',
  [RoleCode.JhsAdmin]: 'blue',
  [RoleCode.ShsAdmin]: 'blue',
  [RoleCode.JhsPrefect]: 'amber',
  [RoleCode.ShsPrefect]: 'amber',
  [RoleCode.Adviser]: 'violet',
  [RoleCode.Teacher]: 'green',
};
apiRequest('get', '/user-details/myDesignations')
  .then((data) => (designations.value = data))
  .catch((e) => (designationsError.value = errorMessage(e, 'Could not load your designations.')));
const advisoryText = computed(() =>
  (designations.value?.advisory || []).map((r) => `${r.grade_level} · ${r.room_section}`).join(', '),
);
</script>

<template>
  <div v-if="loading" class="d-flex justify-center pa-8">
    <v-progress-circular indeterminate size="28" width="3" />
  </div>

  <SettingsSection
    v-else
    title="Profile information"
    description="Update your photo, name and contact details."
    :dirty="isDirty"
    :saving="saving"
    :saved-at="savedAt"
    @save="onSave"
    @reset="reset"
  >
    <ProfilePhoto
      :src="photo"
      :uploading="uploading"
      :removable="hasPhoto"
      :removing="removing"
      @select="onPhoto"
      @remove="onRemovePhoto"
    />

    <!-- Read-only: an administrator grants designations. -->
    <div class="designations">
      <div class="designations__label">Designations</div>
      <v-alert v-if="designationsError" type="error" variant="tonal" density="compact">{{ designationsError }}</v-alert>
      <v-progress-circular v-else-if="!designations" indeterminate size="20" width="2" />
      <template v-else>
        <div v-if="designations.roles.length" class="designations__badges">
          <UiBadge v-for="role in designations.roles" :key="role.id" :variant="ROLE_BADGE[role.code] || 'secondary'">
            {{ role.description }}
          </UiBadge>
        </div>
        <p v-else class="designations__muted">No designations yet.</p>
        <p v-if="advisoryText" class="designations__advisory">
          <v-icon size="16">mdi-google-classroom</v-icon>
          <span><span class="designations__muted">Advisory class:</span> {{ advisoryText }}</span>
        </p>
        <p class="designations__muted designations__hint">Granted by an administrator. Ask them to change these.</p>
      </template>
    </div>

    <v-form ref="formRef" class="form-grid" @submit.prevent="onSave">
      <UiFormField label="First name" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.fname"
          :rules="[required]"
          autocomplete="given-name"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField label="Middle name" v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.mname"
          autocomplete="additional-name"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField label="Last name" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.lname"
          :rules="[required]"
          autocomplete="family-name"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField label="Suffix" v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.suffix"
          placeholder="Jr., III"
          autocomplete="honorific-suffix"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField label="Sex" class="span-2" v-slot="{ id }">
        <v-select :id="id" v-model="form.sex" :items="SEX_OPTIONS" hide-details="auto" />
      </UiFormField>
      <UiFormField label="Birthdate" description="Printed on your class program (Form 29)." v-slot="{ id }">
        <v-text-field :id="id" v-model="form.bdate" type="date" :max="today" autocomplete="bday" hide-details="auto" />
      </UiFormField>
      <UiFormField label="Mobile number" v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.mobile_no"
          :rules="[mobile]"
          inputmode="tel"
          autocomplete="tel"
          placeholder="09171234567"
          hide-details="auto"
        />
      </UiFormField>
      <UiFormField
        label="Email address"
        description="You sign in with this email. Ask an administrator to change it."
        class="span-2"
        v-slot="{ id }"
      >
        <v-text-field :id="id" :model-value="email" readonly disabled hide-details />
      </UiFormField>
    </v-form>
  </SettingsSection>
</template>

<style scoped>
.designations {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.designations__label {
  font-size: 14px;
  font-weight: 500;
}
.designations__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.designations__advisory {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
}
.designations__muted {
  margin: 0;
  font-size: 14px;
  color: var(--muted-foreground);
}
.designations__hint {
  font-size: 13px;
}
</style>
